import * as React from 'react'
// libs
import moment from 'moment'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { formValueSelector } from 'redux-form'
// constants
import { TASKS_TYPES } from 'constants/home'
import { REVIEW_STATUSES } from 'constants/review'
import { ENTITIES, KPI_RESULT_TYPE_VALUES, Forms, Modals } from 'constants/index'
// context
import { TasksListAPIContext } from 'containers/StrategyActions/components/TasksList/layers/API'
import { StrategyActionsBLContext } from 'containers/StrategyActions/layers/BL'
// hooks
import { useShowModal, useCurrentUser } from 'hooks/index'
// types
import { IPlan, IReview } from 'types/graphql'
// utils
import get from 'lodash/get'
import { map, pipe, filter, orderBy } from 'lodash/fp'
import {
  safeGetOr,
  isCurrentUser,
  getOptionByValue,
  getClosestReview,
  generateScaleOptions,
  generateCapturedResults
} from 'utils/index'
import { createFilters } from './utils'
import { isUserReviewer } from 'utils/reviews'

type Props = React.PropsWithChildren<unknown>

export declare namespace NTasksListBL {
  interface IContext {
    plan: IPlan
    tasks: any
    onTaskClick: (task: any) => void
    closestPlanTask: any
    onActionButtonClick: (task: any) => void
  }
}

export const TasksListBLContext = React.createContext<NTasksListBL.IContext>({} as any)

// constants
const getTasksForThisWeek = (task) => {
  const startPeriod = moment().startOf('isoWeek')
  const endPeriod = moment().endOf('isoWeek')

  return moment(task.dueDate).isSameOrAfter(startPeriod, 'day') && moment(task.dueDate).isSameOrBefore(endPeriod, 'day')
}

const TasksListBL: React.FC<Props> = ({ children }) => {
  const {
    data,
    plan,
    updateHandlers: { handleUpdateAction, handleUpdateKPI, handleUpdateObjective }
  } = React.useContext(TasksListAPIContext)

  const { addToCounters, removeFromCounters, isGroupedByPlan, usersFilter, statusFilter, timeFrameFilter } =
    React.useContext(StrategyActionsBLContext)

  const router = useRouter()

  // redux
  const { hideModal: hideActionModal, showModal: showActionModal } = useShowModal({
    modalType: Modals.ACTION_MINIMIZED_MODAL
  })

  const { hideModal: hideKPIModal, showModal: showKPIModal } = useShowModal({ modalType: Modals.KPI_MINIMIZED_MODAL })

  const { hideModal: hideObjectiveModal, showModal: showObjectiveModal } = useShowModal({
    modalType: Modals.OBJECTIVE_MINIMIZED_MODAL
  })

  const { showModal: showSetResultModal, hideModal: hideSetResultModal } = useShowModal({
    modalType: Modals.KPI_SET_RESULT_ROW_MODAL
  })

  const { showModal: showReviewCreateModal } = useShowModal({ modalType: Modals.REVIEW_CREATE_MODAL })

  const { startPeriod, endPeriod, description } = useSelector((state) => ({
    startPeriod: formValueSelector(Forms.HOME_PAGE_FILTER_FORM)(state, 'startPeriod'),
    endPeriod: formValueSelector(Forms.HOME_PAGE_FILTER_FORM)(state, 'endPeriod'),
    description: formValueSelector(Forms.HOME_PAGE_FILTER_FORM)(state, 'description')
  }))

  const currentUser = useCurrentUser()

  const onTaskClick = async (task) => {
    const isPlan = task.entityType === ENTITIES.PLAN
    const isObjective = task.entityType === ENTITIES.PLAN_OBJECTIVE
    const isAction = task.entityType === ENTITIES.PLAN_ACTION
    const isKPI = task.entityType === ENTITIES.PLAN_KPI

    if (isObjective) {
      hideActionModal()
      hideKPIModal()
      showObjectiveModal({ id: task.id })
    }

    if (isAction) {
      hideObjectiveModal()
      hideKPIModal()
      showActionModal({ id: task.id })
    }

    if (isKPI) {
      hideActionModal()
      hideObjectiveModal()
      showKPIModal({ id: task.id })
    }

    if (isPlan) {
      router.push({
        pathname: `/plans/${currentUser.companyId}/edit/${task.id}`
      })
    }
  }

  const onActionButtonClick = async (task) => {
    if (task.type === TASKS_TYPES.REVIEW) {
      showReviewCreateModal({
        entity: task,
        review: task.review,
        isUpdate: get(task, 'review.status', REVIEW_STATUSES.NOT_STARTED) === REVIEW_STATUSES.COMPLETE
      })
    }
    if (task.type === TASKS_TYPES.APPROVAL) {
      showReviewCreateModal({
        entity: task,
        review: task.review,
        isUpdate: true,
        isApprove: !isUserReviewer(task.review, currentUser)
      })
    }
    if (task.type === TASKS_TYPES.ACTION) {
      if (task.entityType === ENTITIES.PLAN_OBJECTIVE) {
        handleUpdateObjective(task)
      } else {
        handleUpdateAction(task)
      }
    }
    if (task.type === TASKS_TYPES.PERIOD) {
      const capturedResults = safeGetOr<any, any, any>([], 'resultTarget.capturedResults')(task)
      const result = safeGetOr<any, any, any>([], 'period.capturedResult.value')(task)
      const isValueExpected = task.resultType === KPI_RESULT_TYPE_VALUES.VALUE

      const scaleValue = get(task, 'resultTypeScale', [])
      const scaleOptions = generateScaleOptions(scaleValue)

      showSetResultModal({
        initialValues: {
          resultValue: isValueExpected ? result : getOptionByValue(result, scaleOptions)
        },
        onSubmit: async (data) => {
          const captures = generateCapturedResults(capturedResults, task.period, isValueExpected, data.resultValue)

          handleUpdateKPI(task, captures)
          hideSetResultModal()
        },
        entity: task
      })
    }
  }

  // constants
  const filters = createFilters({ statusFilter, timeFrameFilter, description, usersFilter, startPeriod, endPeriod })

  const filteredData = React.useMemo(
    () =>
      pipe(
        filter(filters.byStatus),
        filter(filters.byTimeFrame),
        filter(filters.byDescription),
        filter(filters.byAssignedUsers),
        orderBy((task: any) => [!!task.dueDate, moment(task.dueDate).valueOf()], 'asc')
      )(data),
    [data, filters]
  )

  // constants
  const tasks = React.useMemo(
    () => (isGroupedByPlan ? filteredData.filter((task: any) => task.entityType !== ENTITIES.PLAN) : filteredData),
    [filteredData, isGroupedByPlan]
  )

  const closestPlanTask = React.useMemo(
    () =>
      pipe(
        filter<any>((task) => task.entityType === ENTITIES.PLAN && task.type === TASKS_TYPES.REVIEW),
        map((task) => ({ ...task, ...task.review })),
        orderBy((review: IReview & any) => review.dueDate.valueOf(), 'asc'),
        (res) => getClosestReview(false, res)
      )(filteredData),
    [filteredData]
  )

  const assignments = React.useMemo(
    () =>
      pipe(
        filter((task: any) =>
          task.assignedPersons.some((assignedPerson) => isCurrentUser(get(assignedPerson, 'id'), currentUser))
        ),
        // an approval waits for the user right now: its date is the review's due date, not the approval's
        filter((task: any) => task.type === TASKS_TYPES.APPROVAL || getTasksForThisWeek(task)),
        filter((task: any) => task.isOpen),
        (assignments) => assignments.length
      )(data),
    [data]
  )

  const overdue = React.useMemo(
    () =>
      pipe(
        filter((task: any) =>
          task.assignedPersons.some((assignedPerson) => isCurrentUser(get(assignedPerson, 'id'), currentUser))
        ),
        filter((task) => task.isOverdue && task.isOpen),
        (overdue) => overdue.length
      )(data),
    [data]
  )

  React.useEffect(() => {
    const counts = { assignments, overdue }

    addToCounters(counts)

    return () => removeFromCounters(counts)
  }, [assignments, overdue])

  const value = {
    closestPlanTask,
    onActionButtonClick,
    onTaskClick,
    plan,
    tasks
  }

  return <TasksListBLContext.Provider value={value}>{children}</TasksListBLContext.Provider>
}

export default TasksListBL
